import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';

import { isStale } from './lib/build';
import { entryChoice, hasStoredSession, shouldGate } from './lib/settings';

/**
 * The spine, as URLs.
 *
 * Every level is addressable, so a task is a link you can bookmark and come back to — which is
 * what makes "do 1001, then 1004" a navigation rather than a search.
 */
const routes: RouteRecordRaw[] = [
  { path: '/', name: 'home', component: () => import('./pages/HomePage.vue') },
  /*
   * Two routes, one page. `/scratch` is a VERB — it mints a sheet and replaces itself with that
   * sheet's URL — and `/scratch/:sheetId` is the sheet, so a reload, a Back and a bookmark all
   * come back to the one you were on instead of minting another.
   */
  { path: '/scratch', name: 'scratch', component: () => import('./pages/ScratchPage.vue') },
  {
    path: '/scratch/:sheetId',
    name: 'scratch-sheet',
    component: () => import('./pages/ScratchPage.vue'),
    props: true,
  },
  { path: '/log', name: 'log', component: () => import('./pages/LogPage.vue') },
  { path: '/profile', name: 'profile', component: () => import('./pages/ProfilePage.vue') },
  { path: '/settings', name: 'settings', component: () => import('./pages/SettingsPage.vue') },
  /**
   * Signing in is a choice, never a wall. The first visit to `/` is sent here once (see the
   * entry guard at the foot of this file) and one of the three answers is guest, so no route is
   * ever blocked by not having an account — an account only decides whether a join code
   * resolves off this machine.
   */
  { path: '/signin', name: 'signin', component: () => import('./pages/SignInPage.vue') },
  /**
   * Reading a chapter out of a PDF. Not under a course: the course it lands in is one of the
   * things being decided on the page, so hanging the URL off a course would be a lie.
   */
  { path: '/ingest', name: 'ingest', component: () => import('./pages/IngestPage.vue') },
  /* The Book door's URL, and the only way back to the course and chapter boards now that the
     entry screen no longer carries a chapter strip. Phase 3 changes what a chapter OPENS into;
     this list survives it. */
  { path: '/books', name: 'books', component: () => import('./pages/BooksPage.vue') },
  { path: '/calculator', name: 'calculator', component: () => import('./pages/CalculatorPage.vue') },
  { path: '/graph', name: 'graph', component: () => import('./pages/GraphPage.vue') },
  /**
   * Teams sit beside projects: a team is a set of people and the sheets they share, and a shared
   * sheet is addressed by team and sheet so a link to one is a link somebody else can open.
   */
  { path: '/teams', name: 'teams', component: () => import('./pages/TeamsPage.vue'), props: true },
  /**
   * An invitation, as a link. It is the teams page with the join dialog already open and the
   * code already in the field, because eight characters read off somebody's screen and typed
   * back in is the step an invitation exists to remove. The code rides in the PATH rather than
   * a query so the link survives being pasted into a chat that eats query strings.
   */
  {
    path: '/join/:code',
    name: 'join',
    component: () => import('./pages/TeamsPage.vue'),
    props: true,
  },
  {
    path: '/team/:teamId',
    name: 'team',
    component: () => import('./pages/TeamPage.vue'),
    props: true,
  },
  {
    path: '/team/:teamId/sheet/:sheetId',
    name: 'team-sheet',
    component: () => import('./pages/TeamSheetPage.vue'),
    props: true,
  },
  /**
   * An event, at its three moments. One id, three URLs, because a lobby, a run and a review are
   * three different screens and a person coming back to a tab should land on the one the event
   * is actually at — each page forwards to the right sibling when the folded phase disagrees
   * with the URL, so a stale link is self-correcting rather than wrong.
   */
  {
    path: '/team/:teamId/event/:eventId',
    name: 'event',
    component: () => import('./pages/EventLobbyPage.vue'),
    props: true,
  },
  {
    path: '/team/:teamId/event/:eventId/run',
    name: 'event-run',
    component: () => import('./pages/EventRunPage.vue'),
    props: true,
  },
  {
    path: '/team/:teamId/event/:eventId/review',
    name: 'event-review',
    component: () => import('./pages/EventReviewPage.vue'),
    props: true,
  },
  { path: '/course/:courseId', name: 'course', component: () => import('./pages/CoursePage.vue'), props: true },
  { path: '/chapter/:chapterId', name: 'chapter', component: () => import('./pages/ChapterPage.vue'), props: true },
  {
    path: '/chapter/:chapterId/task/:number',
    name: 'task',
    component: () => import('./pages/TaskPage.vue'),
    props: true,
  },

  /**
   * Projects sit beside courses, not under them: work that is not homework has no course to
   * hang off. `/project/:projectId` with no sheet opens the project's first sheet and replaces
   * itself with that URL, so every sheet is a link you can come back to.
   */
  { path: '/projects', name: 'projects', component: () => import('./pages/ProjectsPage.vue') },
  {
    path: '/project/:projectId',
    name: 'project',
    component: () => import('./pages/ProjectPage.vue'),
    props: true,
  },
  {
    path: '/project/:projectId/:sheetId',
    name: 'project-sheet',
    component: () => import('./pages/ProjectPage.vue'),
    props: true,
  },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior: () => ({ top: 0 }),
});

/**
 * A route whose chunk no longer exists.
 *
 * Every deploy renames every hashed file, so a tab left open across one asks for a chunk the
 * host has thrown away. Cloudflare Pages answers that with the SPA shell and a 200, which
 * reaches the page as "Failed to fetch dynamically imported module" in Chrome and as a MIME
 * complaint in Safari — neither of which names the actual cause, and both of which leave a dead
 * page behind. If this tab is running a build that is no longer deployed, a reload IS the fix.
 *
 * Guarded by the path so a chunk that is genuinely broken cannot put the tab in a reload loop,
 * and gated on `isStale` so an offline tab, whose chunks are all in the cache anyway, is left
 * alone.
 */
const RELOAD_MARK = 'euclid:chunk-reload';

router.onError((error, to) => {
  const message = error instanceof Error ? error.message : String(error);
  if (!/dynamically imported module|module script failed|MIME type/i.test(message)) return;
  if (sessionStorage.getItem(RELOAD_MARK) === to.fullPath) return;
  void isStale().then(stale => {
    if (!stale) return;
    sessionStorage.setItem(RELOAD_MARK, to.fullPath);
    location.assign(to.fullPath);
  });
});

router.afterEach(() => { sessionStorage.removeItem(RELOAD_MARK); });

/**
 * The first visit, and only the first.
 *
 * `CLAUDE.md` is unambiguous — nothing may block first paint on a network call — and this guard
 * makes none. It reads `localStorage` synchronously, and only when that says nobody has ever
 * answered does it look at IndexedDB, which is the same local store the workspace itself is
 * kept in. A sign-up WALL would break the rule; a choice screen that offers guest does not, and
 * guest is why it does not.
 *
 * Three narrowings, each one deliberate:
 *
 *  - **Only the front door.** `/` is where somebody arrives; a deep link is somebody who came
 *    for a specific thing, and putting a sign-in screen in front of `/join/:code` would break an
 *    invitation to make a point about accounts.
 *  - **Only the first navigation of the page load.** After that the answer is settled for the
 *    tab, so nothing you do inside the app can drop you back onto the gate.
 *  - **Only a device with nothing on it.** A recorded choice, a session, or any sheet, project
 *    or log entry in IndexedDB all send you straight through.
 */
let entrySettled = false;

router.beforeEach(to => {
  if (entrySettled) return true;
  // Set before the redirect: it re-enters this guard, and a flag set afterwards would send it
  // round again.
  entrySettled = true;
  // Deep links are never gated. A sign-in screen in front of /join/:code breaks an invitation to
  // make a point about accounts.
  if (to.path !== '/') return true;
  return shouldGate(entryChoice(), hasStoredSession()) ? { path: '/signin', replace: true } : true;
});
